import type { CallCapabilitiesContract } from '@signalwire/client'
import type { StateCreator } from 'zustand'
import { create } from 'zustand'

interface MemberCapabilities {
  memberAudioFlagsCapability: boolean
  memberDNDCapabilityOff: boolean
  memberDNDCapabilityOn: boolean
  memberHandRaiseCapabilityOff: boolean
  memberHandRaiseCapabilityOn: boolean
  memberMicrophoneCapabilityOff: boolean
  memberMicrophoneCapabilityOn: boolean
  memberMicrophoneSensitivityCapability: boolean
  memberMicrophoneVolumeCapability: boolean
  memberSpeakerVolumeCapability: boolean
  memberVideoCapabilityOff: boolean
  memberVideoCapabilityOn: boolean
}

interface SelfCapabilities {
  selfAudioFlagsCapability: boolean
  selfDNDCapabilityOff: boolean
  selfDNDCapabilityOn: boolean
  selfHandRaiseCapabilityOff: boolean
  selfHandRaiseCapabilityOn: boolean
  selfMicrophoneCapabilityOff: boolean
  selfMicrophoneCapabilityOn: boolean
  selfMicrophoneSensitivityCapability: boolean
  selfMicrophoneVolumeCapability: boolean
  selfPositionCapability: boolean
  selfRoomLockCapabilityOff: boolean
  selfRoomLockCapabilityOn: boolean
  selfScreenShareCapability: boolean
  selfSetLayoutCapability: boolean
  selfSpeakerVolumeCapability: boolean
  selfVideoCapabilityOff: boolean
  selfVideoCapabilityOn: boolean
}

type CapabilitiesState = MemberCapabilities & SelfCapabilities

interface State extends CapabilitiesState {
  capabilities: CallCapabilitiesContract | undefined
}

interface Actions {
  actions: {
    setCapabilities: (capabilities: CallCapabilitiesContract) => void
  }
}

type Store = Actions & State

const initialState: State = {
  capabilities: undefined,
  memberAudioFlagsCapability: false,
  memberDNDCapabilityOff: false,
  memberDNDCapabilityOn: false,
  memberHandRaiseCapabilityOff: false,
  memberHandRaiseCapabilityOn: false,
  memberMicrophoneCapabilityOff: false,
  memberMicrophoneCapabilityOn: false,
  memberMicrophoneSensitivityCapability: false,
  memberMicrophoneVolumeCapability: false,
  memberSpeakerVolumeCapability: false,
  memberVideoCapabilityOff: false,
  memberVideoCapabilityOn: false,
  selfAudioFlagsCapability: false,
  selfDNDCapabilityOff: false,
  selfDNDCapabilityOn: false,
  selfHandRaiseCapabilityOff: false,
  selfHandRaiseCapabilityOn: false,
  selfMicrophoneCapabilityOff: false,
  selfMicrophoneCapabilityOn: false,
  selfMicrophoneSensitivityCapability: false,
  selfMicrophoneVolumeCapability: false,
  selfPositionCapability: false,
  selfRoomLockCapabilityOff: false,
  selfRoomLockCapabilityOn: false,
  selfScreenShareCapability: false,
  selfSetLayoutCapability: false,
  selfSpeakerVolumeCapability: false,
  selfVideoCapabilityOff: false,
  selfVideoCapabilityOn: false,
}

const mapCapabilitiesToState = (capabilities: CallCapabilitiesContract) => {
  const { lock, self, screenshare, setLayout, member } = capabilities

  const selfCapabilities = {
    selfAudioFlagsCapability: self.audioFlags,
    selfDNDCapabilityOff: self.deaf.off,
    selfDNDCapabilityOn: self.deaf.on,
    selfHandRaiseCapabilityOff: self.raisehand.off,
    selfHandRaiseCapabilityOn: self.raisehand.on,
    selfMicrophoneCapabilityOff: self.muteAudio.off,
    selfMicrophoneCapabilityOn: self.muteAudio.on,
    selfMicrophoneSensitivityCapability: self.microphoneSensitivity,
    selfMicrophoneVolumeCapability: self.microphoneVolume,
    selfPositionCapability: self.position,
    selfRoomLockCapabilityOff: lock.off,
    selfRoomLockCapabilityOn: lock.on,
    selfScreenShareCapability: screenshare,
    selfSetLayoutCapability: setLayout,
    selfSpeakerVolumeCapability: self.speakerVolume,
    selfVideoCapabilityOff: self.muteVideo.off,
    selfVideoCapabilityOn: self.muteVideo.on,
  } satisfies SelfCapabilities

  const memberCapabilities = {
    memberAudioFlagsCapability: member.audioFlags,
    memberDNDCapabilityOff: member.deaf.off,
    memberDNDCapabilityOn: member.deaf.on,
    memberHandRaiseCapabilityOff: member.raisehand.off,
    memberHandRaiseCapabilityOn: member.raisehand.on,
    memberMicrophoneCapabilityOff: member.muteAudio.off,
    memberMicrophoneCapabilityOn: member.muteAudio.on,
    memberMicrophoneSensitivityCapability: member.microphoneSensitivity,
    memberMicrophoneVolumeCapability: member.microphoneVolume,
    memberSpeakerVolumeCapability: member.speakerVolume,
    memberVideoCapabilityOff: member.muteVideo.off,
    memberVideoCapabilityOn: member.muteVideo.on,
  } satisfies MemberCapabilities

  return {
    ...selfCapabilities,
    ...memberCapabilities,
  } satisfies CapabilitiesState
}

const stateCreatorFn: StateCreator<Store> = (set, _get) => ({
  ...initialState,
  actions: {
    setCapabilities: (capabilities: CallCapabilitiesContract) => {
      // TODO: do we need to save all the capabilities? or just the ones we use in the app?
      set({
        capabilities,
        ...mapCapabilitiesToState(capabilities),
      })
    },
  },
})

export const useCapabilitiesStore = create<Store>()(stateCreatorFn)
export const useCapabilitiesStoreActions = () =>
  useCapabilitiesStore.getState().actions
